import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage, SUPPORTED_LANGUAGES, SupportedLanguage } from '@/services/languageService';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe } from 'lucide-react';

/**
 * 语言切换组件，允许用户在不同语言之间切换
 * @returns {JSX.Element} 语言切换下拉菜单
 */
const LanguageSwitcher = () => {
  const { currentLanguage, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 查找当前语言对象
  const currentLangObj = SUPPORTED_LANGUAGES.find(lang => lang.code === currentLanguage);

  /**
   * 处理语言切换
   * @param {SupportedLanguage} langCode - 新语言代码
   */
  const handleLanguageChange = (langCode: SupportedLanguage) => {
    // 获取当前路径
    let currentPath = location.pathname;
    
    // 如果当前在根路径
    if (currentPath === '/' || currentPath === '') {
      if (langCode === 'en') {
        navigate('/', { replace: true });
      } else {
        navigate(`/${langCode}`, { replace: true });
      }
      // 设置语言状态
      setLanguage(langCode);
      return;
    }
    
    // 检查当前路径是否包含语言代码
    const pathParts = currentPath.split('/').filter(part => part !== '');
    
    // 判断第一部分是否是语言代码
    const isFirstPartLanguage = SUPPORTED_LANGUAGES.some(lang => lang.code === pathParts[0]);
    
    // 构建新路径
    let newPath;
    if (isFirstPartLanguage) {
      // 当前路径包含语言代码，替换为新语言代码
      const remainingPath = pathParts.slice(1).join('/');
      if (langCode === 'en') {
        newPath = remainingPath ? `/${remainingPath}` : '/';
      } else {
        newPath = remainingPath ? `/${langCode}/${remainingPath}` : `/${langCode}`;
      }
    } else {
      // 当前路径不包含语言代码
      if (langCode === 'en') {
        newPath = currentPath;
      } else {
        newPath = `/${langCode}${currentPath}`;
      }
    }
    
    // 先导航到新路径（这会触发 LanguageProvider 的 useEffect）
    navigate(newPath, { replace: true });
    // 然后更新语言状态（作为备份，确保同步）
    setLanguage(langCode);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="px-3 h-10 flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <Globe className="h-4 w-4" />
          <span className="text-sm font-medium">
            {currentLangObj?.nativeName}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        {SUPPORTED_LANGUAGES.map(language => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={`
              cursor-pointer px-3 py-2
              ${currentLanguage === language.code ? 'bg-gray-100 dark:bg-gray-800' : ''}
            `}
          >
            <div className="flex flex-col">
              <span className="font-medium">{language.nativeName}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {language.name}
              </span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
